import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import { ArrowRight, BookOpen, Flame, Heart, Sparkles, ArrowDown } from "lucide-react";

const HERO_BG =
    "https://customer-assets-m6fa6gv7.emergentagent.net/job_recipe-timeline/artifacts/nn5tajdl_ChatGPT%20Image%20Aug%2017%2C%202026%2C%2004_37_11%20PM.png";
const ARCHIVE_BG =
    "https://static.prod-images.emergentagent.com/jobs/ca15f75e-ad30-44ae-956b-53f64afe519e/images/18c4802e009a11c6d1d82d43ade5886b41237748935a547662c259504a22b3de.png";

const CATEGORY_IMAGES = {
    "Cyprus": "https://images.unsplash.com/photo-1564759224907-65b945ff0e84?auto=format&fit=crop&w=900&q=80",
    "Greece": "https://images.unsplash.com/photo-1530841377377-3ff06c0ca713?auto=format&fit=crop&w=900&q=80",
    "Village Recipes": "https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&w=900&q=80",
    "Seafood": "https://images.unsplash.com/photo-1559737558-2f5a35f4523b?auto=format&fit=crop&w=900&q=80",
    "Street Food": "https://images.unsplash.com/photo-1603360946369-dc9bb6258143?auto=format&fit=crop&w=900&q=80",
    "Easter Recipes": "https://images.unsplash.com/photo-1587324438673-56c78a866b15?auto=format&fit=crop&w=900&q=80",
    "Vegetarian": "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=900&q=80",
    "Desserts": "https://images.pexels.com/photos/28978283/pexels-photo-28978283.jpeg?auto=compress&cs=tinysrgb&w=900",
};

const Home = () => {
    const [recipes, setRecipes] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/recipes"),
            api.get("/products"),
            api.get("/categories"),
        ]).then(([r, p, c]) => {
            setRecipes(r.data);
            setProducts(p.data.products);
            setCategories(c.data.categories);
        }).catch(() => {});
    }, []);

    const featured = recipes.filter((r) => r.featured).slice(0, 4);
    const stories = recipes
        .filter((r) => r.featured)
        .slice()
        .sort((a, b) => {
            const ea = a.episode ?? Number.POSITIVE_INFINITY;
            const eb = b.episode ?? Number.POSITIVE_INFINITY;
            return ea - eb;
        });

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                data-testid="home-hero"
                className="relative h-[100vh] min-h-[680px] flex items-end overflow-hidden"
            >
                <div className="absolute inset-0 overflow-hidden">
                    <div
                        className="absolute inset-0 bg-cover hl-slow-zoom"
                        style={{ backgroundImage: `url(${HERO_BG})`, backgroundPosition: "center 28%" }}
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-hl-brown/30 via-hl-brown/35 to-hl-brown/85" />

                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
                    <p
                        className="hl-fade-up font-script text-[#E8D9B5] text-3xl md:text-4xl mb-4"
                        data-testid="hero-kicker"
                    >
                        a digital cookbook & cultural archive —
                    </p>
                    <h1
                        className="hl-fade-up hl-delay-1 font-serif text-hl-bg text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.98] tracking-tight max-w-5xl"
                        data-testid="hero-title"
                    >
                        Traditional <span className="italic">Greek</span>
                        <br />
                        & <span className="italic">Cypriot</span> Recipes
                    </h1>
                    <p
                        className="hl-fade-up hl-delay-2 mt-8 text-hl-bg/85 text-xl md:text-2xl max-w-2xl leading-relaxed font-serif italic"
                        data-testid="hero-subtitle"
                    >
                        Food, history, and culture preserved through storytelling.
                    </p>
                    <div className="hl-fade-up hl-delay-3 mt-12 flex flex-wrap gap-4 items-center">
                        <Link
                            to="/recipes"
                            data-testid="hero-cta-recipes"
                            className="inline-flex items-center gap-3 px-9 py-4 bg-hl-secondary text-hl-bg hover:bg-hl-secondaryDeep hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                        >
                            Explore Recipes <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </Link>
                        <Link
                            to="/stories"
                            data-testid="hero-cta-stories"
                            className="inline-flex items-center gap-3 px-9 py-4 bg-transparent border border-hl-bg/50 text-hl-bg hover:bg-hl-bg/10 hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                        >
                            Taste the Story
                        </Link>
                    </div>

                    <div className="hl-fade-up hl-delay-5 absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-hl-bg/70">
                        <span className="text-[10px] uppercase tracking-[0.3em]">scroll</span>
                        <ArrowDown className="w-4 h-4 animate-bounce" strokeWidth={1.5} />
                    </div>
                </div>
            </section>

            {/* FEATURED RECIPES */}
            <section
                data-testid="featured-section"
                className="max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-36"
            >
                <div className="hl-divider mb-8 max-w-md mx-auto">
                    <span className="font-script text-3xl">today on the table</span>
                </div>
                <h2 className="font-serif text-center text-4xl md:text-6xl text-hl-brown leading-tight max-w-3xl mx-auto">
                    Featured <span className="italic">recipes</span> from the archive
                </h2>
                <p className="text-center text-hl-muted mt-5 max-w-xl mx-auto">
                    The dishes our grandparents would recognize — every one with its
                    backstory and a place in our family table.
                </p>

                <div className="mt-16 grid gap-12 md:gap-8 sm:grid-cols-2 lg:grid-cols-4">
                    {featured.map((r, i) => (
                        <RecipeCard key={r.id} recipe={r} index={i} />
                    ))}
                </div>

                <div className="mt-14 flex justify-center">
                    <Link
                        to="/recipes"
                        data-testid="featured-see-all"
                        className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-hl-brown hover:text-hl-secondary hl-link"
                    >
                        See all recipes <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
            </section>

            {/* TASTE THE STORY */}
            <section
                data-testid="taste-the-story"
                className="relative py-28 md:py-40 hl-grain"
                style={{
                    backgroundImage: `linear-gradient(rgba(58,42,31,0.92), rgba(58,42,31,0.93)), url(${ARCHIVE_BG})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundAttachment: "fixed",
                }}
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 text-hl-bg">
                    <div className="hl-divider mb-8 max-w-md text-hl-secondary/70">
                        <span className="font-script text-3xl text-[#E8D9B5]">taste the story —</span>
                    </div>
                    <h2 className="font-serif text-4xl md:text-6xl leading-tight max-w-3xl">
                        Every dish carries a <span className="italic">village</span> inside it.
                    </h2>
                    <p className="text-hl-bg/75 mt-6 max-w-xl text-lg leading-relaxed">
                        Some recipes are too important to be told as a list of ingredients.
                        These are the dishes whose stories we'll never let go of.
                    </p>

                    <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
                        {stories.map((r, i) => (
                            <Link
                                key={r.id}
                                to={`/recipes/${r.slug}`}
                                data-testid={`story-card-${r.slug}`}
                                className={`group hl-fade-up hl-delay-${Math.min(5, i + 1)} block`}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden rounded-sm hl-shadow-deep">
                                    <img
                                        src={r.hero_image}
                                        alt={r.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    />
                                    {r.episode ? (
                                        <div
                                            data-testid={`story-card-episode-${r.slug}`}
                                            className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-hl-brown/85 backdrop-blur-sm border border-[#E8D9B5]/30 rounded-sm"
                                        >
                                            <span className="font-script text-[#E8D9B5] text-base leading-none">ep.</span>
                                            <span className="font-serif text-hl-bg text-sm tracking-[0.18em] leading-none">
                                                {String(r.episode).padStart(2, "0")}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                                <p className="mt-5 text-[10px] uppercase tracking-[0.25em] text-hl-bg/60">
                                    {r.origin} · {r.episode ? `Episode ${String(r.episode).padStart(2, "0")}` : "A story"}
                                </p>
                                <h3 className="mt-2 font-serif text-2xl md:text-3xl text-hl-bg group-hover:text-[#E8D9B5] transition-colors duration-500 leading-tight">
                                    {r.title}
                                </h3>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section
                data-testid="categories-section"
                className="max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-36"
            >
                <div className="hl-divider mb-8 max-w-md mx-auto">
                    <span className="font-script text-3xl">browse the kitchen</span>
                </div>
                <h2 className="font-serif text-center text-4xl md:text-6xl text-hl-brown leading-tight">
                    Recipe <span className="italic">categories</span>
                </h2>

                <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                    {categories.map((c, i) => (
                        <Link
                            key={c}
                            to={`/recipes?category=${encodeURIComponent(c)}`}
                            data-testid={`category-tile-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`group relative overflow-hidden aspect-[4/5] rounded-sm hl-shadow-warm hl-fade-up hl-delay-${Math.min(5, (i % 5) + 1)}`}
                        >
                            <img
                                src={CATEGORY_IMAGES[c] || HERO_BG}
                                alt={c}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-hl-brown/85 via-hl-brown/35 to-transparent transition-opacity duration-500 group-hover:from-hl-brown/95" />
                            <div className="absolute inset-x-0 bottom-0 p-5">
                                <h3 className="font-serif text-2xl md:text-3xl text-hl-bg leading-tight">{c}</h3>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-hl-bg/70 mt-1">
                                    Browse →
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* APHRODITE'S ROOTS — products */}
            <section
                data-testid="aphrodites-teaser"
                className="bg-hl-tinted/60 py-28 md:py-36 hl-grain"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
                    <div className="lg:col-span-5">
                        <p className="font-script text-3xl text-hl-secondary mb-2">coming soon —</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-hl-brown leading-[1.05]">
                            Aphrodite's <span className="italic">Roots</span>
                        </h2>
                        <p className="mt-6 text-hl-muted leading-relaxed text-lg">
                            We're starting with one ingredient. The wild oregano we cook with
                            every day — picked by hand from the hills of Cyprus, dried slowly
                            in the shade, packed into small 40g pouches. The rigani that
                            perfumes every plate in this archive.
                        </p>
                        <Link
                            to="/aphrodites-roots"
                            data-testid="aphrodites-cta"
                            className="inline-flex items-center gap-3 mt-9 px-7 py-3.5 border border-hl-brown text-hl-brown hover:bg-hl-brown hover:text-hl-bg hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                        >
                            See the first batch <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Link>
                    </div>

                    <div className="lg:col-span-7 grid grid-cols-1 gap-4 md:gap-6">
                        {products.slice(0, 1).map((p, i) => (
                            <div
                                key={p.id}
                                className={`hl-fade-up hl-delay-${Math.min(5, i + 1)} relative aspect-[4/5] overflow-hidden rounded-sm bg-hl-bg hl-shadow-warm`}
                            >
                                <img
                                    src={p.image}
                                    alt={p.name}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-1000 hover:scale-110"
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-hl-brown/90 to-transparent">
                                    <p className="font-script text-2xl text-[#E8D9B5] leading-none mb-1">first batch —</p>
                                    <p className="font-serif text-hl-bg text-2xl md:text-3xl leading-tight">{p.name}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ABOUT */}
            <section
                data-testid="home-about"
                className="max-w-4xl mx-auto px-6 lg:px-10 py-28 md:py-36 text-center"
            >
                <div className="hl-divider mb-8 max-w-md mx-auto">
                    <span className="font-script text-3xl">our story</span>
                </div>
                <p className="font-serif text-3xl md:text-5xl text-hl-brown italic leading-[1.25]">
                    "IcookGreek was created to preserve and share the flavors,
                    stories, and traditions of Greek and Cypriot cuisine through
                    modern storytelling."
                </p>
                <div className="mt-10 flex flex-wrap gap-8 justify-center">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-hl-muted">
                        <BookOpen className="w-4 h-4 text-hl-primary" strokeWidth={1.5} /> Stories
                    </div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-hl-muted">
                        <Flame className="w-4 h-4 text-hl-secondary" strokeWidth={1.5} /> Tradition
                    </div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-hl-muted">
                        <Heart className="w-4 h-4 text-hl-primary" strokeWidth={1.5} /> Patience
                    </div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-hl-muted">
                        <Sparkles className="w-4 h-4 text-hl-gold" strokeWidth={1.5} /> Memory
                    </div>
                </div>
                <Link
                    to="/about"
                    className="inline-block mt-10 hl-link text-[11px] uppercase tracking-[0.3em] text-hl-secondary"
                >
                    More about us →
                </Link>
            </section>
        </div>
    );
};

export default Home;
